import { type V2, angleDiff, angleTo, dist, lerp, smoothstep } from '../../core/math';
import type { Rng } from '../../core/rng';
import { SEA_HAZARDS } from '../../content/zones/sea';
import { LIGHTNING_DAMAGE, LIGHTNING_HIT_RADIUS, STORM_DPS, STORM_WIND } from './data';
import { bearingDeg } from './ship';

/**
 * Sea hazards (centres and radii from the sea layout): storms batter the hull,
 * shove the ship around and throw lightning; fog banks hide the way. Pure
 * functions of position so every peer agrees without networking.
 */

export type HazardKind = 'storm' | 'fog';

export interface Hazard {
  id: string;
  kind: HazardKind;
  x: number;
  z: number;
  r: number;
}

export const HAZARDS: readonly Hazard[] = SEA_HAZARDS;

/** Storm strength: 0 at the edge and outside, 1 in the eye (inner 35%). */
export function stormIntensity(h: Hazard, p: V2): number {
  return 1 - smoothstep(h.r * 0.35, h.r, dist(h, p));
}

/** Fog thickness: 0 at the edge and outside, 1 in the inner 45%. */
export function fogDensity(h: Hazard, p: V2): number {
  return 1 - smoothstep(h.r * 0.45, h.r, dist(h, p));
}

export interface HazardSample {
  storm: number;
  fog: number;
  stormHazard: Hazard | null;
  fogHazard: Hazard | null;
}

/** Strongest storm and fog at a point. */
export function sampleHazards(p: V2, list: readonly Hazard[] = HAZARDS): HazardSample {
  const out: HazardSample = { storm: 0, fog: 0, stormHazard: null, fogHazard: null };
  for (const h of list) {
    if (h.kind === 'storm') {
      const k = stormIntensity(h, p);
      if (k > out.storm) {
        out.storm = k;
        out.stormHazard = h;
      }
    } else {
      const k = fogDensity(h, p);
      if (k > out.fog) {
        out.fog = k;
        out.fogHazard = h;
      }
    }
  }
  return out;
}

/** Storm current (m/s): a swirl around the eye plus an outward shove, scaled by intensity. */
export function stormCurrent(h: Hazard, p: V2, intensity = stormIntensity(h, p)): V2 {
  if (intensity <= 0) return { x: 0, z: 0 };
  const dx = p.x - h.x;
  const dz = p.z - h.z;
  const d = Math.hypot(dx, dz);
  const ox = d > 1e-3 ? dx / d : 1;
  const oz = d > 1e-3 ? dz / d : 0;
  const k = STORM_WIND * intensity;
  return { x: (-oz * 0.8 + ox * 0.45) * k, z: (ox * 0.8 + oz * 0.45) * k };
}

/** Hull lost over `dt` seconds at a storm intensity (edges 40%, the eye 100%). */
export function stormDamage(intensity: number, resist: number, dt: number): number {
  if (intensity <= 0) return 0;
  return STORM_DPS * (0.4 + 0.6 * intensity) * (1 - resist) * dt;
}

export const lightningDamage = (resist: number): number => LIGHTNING_DAMAGE * (1 - resist);
export const lightningHits = (strike: V2, ship: V2): boolean => dist(strike, ship) <= LIGHTNING_HIT_RADIUS;

/**
 * Lightning around a ship inside a storm: strikes come faster in the eye, and
 * some land right on the hull. Seeded, so tests (and replays) are exact.
 */
export class LightningClock {
  private t: number;

  constructor(private readonly rng: Rng) {
    this.t = rng.range(1.5, 3);
  }

  /** Returns where a bolt lands this step, if one does. */
  update(dt: number, intensity: number, at: V2): V2 | null {
    if (intensity < 0.15) {
      this.t = Math.max(this.t, 1.2);
      return null;
    }
    this.t -= dt;
    if (this.t > 0) return null;
    this.t = lerp(6, 2.4, intensity) * this.rng.range(0.7, 1.3);
    const a = this.rng.range(0, Math.PI * 2);
    const r = this.rng.chance(0.3 + 0.25 * intensity) ? this.rng.range(0, 3) : this.rng.range(6, 16);
    return { x: at.x + Math.sin(a) * r, z: at.z + Math.cos(a) * r };
  }
}

export interface HazardWarning {
  hazard: Hazard;
  inside: boolean;
  /** Metres to the hazard's edge (0 inside). */
  distance: number;
  /** Compass bearing to the hazard centre. */
  bearing: number;
  /** The hazard lies within 50° of the bow. */
  ahead: boolean;
}

/** The hazard the helm should hear about: one we're in (storms first), else the nearest edge within `range`. */
export function hazardWarning(p: V2, heading: number, range = 90, list: readonly Hazard[] = HAZARDS): HazardWarning | null {
  let best: HazardWarning | null = null;
  for (const h of list) {
    const d = dist(h, p);
    const inside = d < h.r;
    const edge = Math.max(0, d - h.r);
    if (!inside && edge > range) continue;
    const w: HazardWarning = { hazard: h, inside, distance: edge, bearing: bearingDeg(h.x - p.x, h.z - p.z), ahead: inside || Math.abs(angleDiff(heading, angleTo(p, h))) < (50 * Math.PI) / 180 };
    if (!best) best = w;
    else if (w.inside !== best.inside) best = w.inside ? w : best;
    else if (w.inside) best = h.kind === 'storm' && best.hazard.kind !== 'storm' ? w : best;
    else if (w.distance < best.distance) best = w;
  }
  return best;
}
